import type { ControllerInfo, Participant } from '@br/protocol';
import { cx } from '../components/ui';

export interface PresenceProps {
  participants: Participant[];
  controllerId?: string;
  /** Holder summary; shown as a ghost chip when they are disconnected but still hold control. */
  controller?: ControllerInfo;
  meId?: string;
}

/**
 * Compact chips for everyone viewing the profile; the controller gets a filled chip and marker.
 * Control is reserved across disconnects, so an absent holder still appears (dashed) so everyone
 * can see who they must take it from.
 */
export function Presence({ participants, controllerId, controller, meId }: PresenceProps) {
  const sorted = [...participants].sort((a, b) => a.joinedAt - b.joinedAt);
  const awayHolder = controller && !controller.connected ? controller : undefined;
  return (
    <div className="flex flex-wrap items-center gap-1" aria-label="참여자">
      {awayHolder && (
        <span
          title={`${awayHolder.kind === 'operator' ? '운영자' : '고객'} · 제어 중 · 연결 끊김`}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary px-2 py-0.5 text-xs text-primary"
        >
          <span className="size-1.5 rounded-full border border-primary" />
          {awayHolder.name}
          <span className="text-primary/70">(연결 끊김)</span>
        </span>
      )}
      {sorted.map((p) => {
        const controller = p.participantId === controllerId;
        const me = p.participantId === meId;
        return (
          <span
            key={p.participantId}
            title={`${p.kind === 'operator' ? '운영자' : '고객'}${controller ? ' · 제어 중' : ''}${me ? ' · 나' : ''}`}
            className={cx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              controller
                ? 'border-primary bg-primary text-white'
                : 'border-line bg-surface text-ink',
            )}
          >
            <span
              className={cx(
                'size-1.5 rounded-full',
                p.kind === 'operator' ? 'bg-primary-soft' : 'bg-warning',
                controller && 'bg-white',
              )}
            />
            {p.name}
            {me && <span className={controller ? 'text-white/80' : 'text-ink-muted'}>(나)</span>}
          </span>
        );
      })}
      {sorted.length === 0 && !awayHolder && (
        <span className="text-xs text-ink-muted">참여자 없음</span>
      )}
    </div>
  );
}
